import React, { useState, useEffect, useMemo, useCallback, useRef, memo, lazy, Suspense } from 'react';
import { Header } from './components/Header.jsx';
import { TariffCard } from './components/cards/TariffCard.jsx';
import { Dashboard } from './components/Dashboard.jsx';
import { SpielgeldRechner } from './components/SpielgeldRechner.jsx';
import BruttoNettoRechner from './components/BruttoNettoRechner.jsx';

// Optimierte Imports
import { DevicePerformance, debounce, throttle } from './utils/performance.js';
import { EVENT_DEFINITIONS, LUCKY_ITEMS, detectCurrentEvent } from './config/events.js';
import { useEventSettings } from './hooks/useEventSettings.js';
import { SnowEffect } from './components/effects/SnowEffect.jsx';
import { BubbleEffect } from './components/effects/BubbleEffect.jsx';
import { CardBubblesEffect, CardSnowEffect } from './components/effects/CardEffectsOptimized.jsx';
import { SilvesterCountdownWidget } from './components/widgets/SilvesterCountdown.jsx';
import DatePicker from './components/DatePicker.jsx';

// Lazy Loading - Admin Panel wird nur bei Bedarf geladen (-100KB initial)
const EventAdminPanel = lazy(() => import('./components/admin/EventAdminPanel.jsx').then(m => ({ default: m.EventAdminPanel })));

import { AppRoutes } from './routes/AppRoutes.jsx';
import { AppShell, MainContent } from './layouts/AppShell.jsx';
import { LiveViewPanel } from './components/panels/LiveViewPanel.jsx';
import { TariffEditModal } from './components/modals/TariffEditModal.jsx';
import { PageRenderer } from './components/PageRenderer.jsx';

import { capitalize, ConfettiEffect, LeafEffect, EasterEffect, CheckIcon, PhoneIcon, WifiIcon, getCategoryTag } from './utils/appHelpers.jsx';
import { useAuth } from './hooks/useAuth.js';
import { MarketProvider } from './hooks/useMarket.jsx';
import { LoginScreen } from './components/LoginScreen.jsx';

function App() {
  // === PHASE 6: DYNAMISCHES ROUTING (TEF DESIGNER) ===
  const [routedPageId, setRoutedPageId] = useState(null);
  const [isRouting, setIsRouting] = useState(true);
  const [useLegacyMode, setUseLegacyMode] = useState(false);

  useEffect(() => {
    // 1. Check auf 'legacy=1' Query-Parameter (Fallback / Safemode)
    const params = new URLSearchParams(window.location.search);
    if (params.get('legacy') === '1') {
      setUseLegacyMode(true);
      setIsRouting(false);
      return;
    }

    // 2. Pathname parsen und API nach Seite fragen
    const currentPath = window.location.pathname;
    let slug = currentPath.substring(1); 
    if (!slug || slug === '') { slug = 'home'; }

    // Sonderfälle direkt in die Legacy App werfen, falls gewünscht
    if (slug.includes('spielgeld') || slug.includes('bruttonetto')) {
      setUseLegacyMode(true);
      setIsRouting(false);
      return;
    }

    const fetchPageBySlug = async () => {
      try {
        const res = await fetch(`/api/admin/designer/pages/slug/${slug}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.page) {
            setRoutedPageId(data.page.id);
            setIsRouting(false);
            return;
          }
        }
        console.warn(`[TEF Router] Seite /${slug} nicht im neuen System gefunden. Fallback auf Legacy Mode.`);
        setUseLegacyMode(true);
      } catch (e) {
        console.error('[TEF Router] Fehler beim Seiten-Routing:', e);
        setUseLegacyMode(true);
      }
      setIsRouting(false);
    };

    fetchPageBySlug();
  }, []);

  // ==================== NAVIGATION STATE (LEGACY) ====================
  const [activeSection, setActiveSection] = useState(() => {
    try {
      const saved = sessionStorage.getItem('tefcms_active_section');
      if (saved) return saved;
      
      if (window.location.pathname.startsWith('/p/')) return window.location.pathname;
      const path = window.location.pathname.toLowerCase();
      if (path.includes('spielgeld')) return 'spielgeld';
      if (path.includes('bruttonetto') || path.includes('brutto-netto')) return 'bruttonetto';
    } catch(e) {}
    return 'dashboard';
  });

  useEffect(() => {
    sessionStorage.setItem('tefcms_active_section', activeSection);
  }, [activeSection]);
  
  // ==================== EVENT-SYSTEM STATE (Optimiert mit Hook) ====================
  const { settings: eventSettings, updateSettings } = useEventSettings();
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  
  // Event-Einstellungen speichern (Auto-Save durch Hook)
  const handleEventSettingsChange = useCallback((newSettings) => {
    updateSettings(newSettings);
  }, [updateSettings]);
  
  // Hintergrundfarbe anwenden
  useEffect(() => {
    const { backgroundColors } = eventSettings;
    if (backgroundColors.isGradient) {
      document.body.style.background = `linear-gradient(${backgroundColors.angle}deg, ${backgroundColors.color1}, ${backgroundColors.color2})`;
    } else {
      document.body.style.background = backgroundColors.color1;
    }
    document.body.style.minHeight = '100vh';
  }, [eventSettings]);

  // Aktive Ansicht: immer Standard, keine Persistenz
  const [activeSegment, setActiveSegment] = useState("mobile");
  // Brand-Bereich Umschalter: o2 (blau) vs Super Select (rot) vs alle
  const [activeBrand, setActiveBrand] = useState("all");
  // Mehrfachauswahl: aktive Karten-IDs für Detailanzeige
  const [activeCardIds, setActiveCardIds] = useState([]);
  
  // NEU: Tarif-Mehrfachauswahl für Vergleich/Druck
  const [selectedTariffIds, setSelectedTariffIds] = useState([]);
  const [liveViewOpen, setLiveViewOpen] = useState(false);
  
  // Tarif zur Auswahl hinzufügen/entfernen
  const handleSelectToggle = useCallback((tariffId) => {
    setSelectedTariffIds(prev => {
      if (prev.includes(tariffId)) {
        return prev.filter(id => id !== tariffId);
      } else {
        return [...prev, tariffId];
      }
    });
  }, []);
  
  // Alle Auswahlen löschen
  const handleClearSelection = useCallback(() => {
    setSelectedTariffIds([]);
    setLiveViewOpen(false);
  }, []);
  
  // Einzeldruck eines Tarifs
  const handlePrintSingle = useCallback((tariffId) => {
    window.open(`druckansicht.html?tarife=${tariffId}`, '_blank');
  }, []);
  const [activeMobileGroup, setActiveMobileGroup] = useState("alle");
  // Kombi-Gruppen entfallen – nur Mobile 50% anzeigen
  const [activeKombiGroup, setActiveKombiGroup] = useState("classic");
  const [activeMyHomeTech, setActiveMyHomeTech] = useState("all");
  // Info-Overlay entfernt
  // Suchfunktion
  const [searchQuery, setSearchQuery] = useState("");
  // Ajax-Suche mit Debounce
  const [ajaxSearchInput, setAjaxSearchInput] = useState("");
  const [ajaxSearchResults, setAjaxSearchResults] = useState([]);
  const [showAjaxDropdown, setShowAjaxDropdown] = useState(false);
  const ajaxSearchRef = React.useRef(null);
  const ajaxTimerRef = React.useRef(null);

  const [tariffs, setTariffs] = useState([]);
  const [editingTariff, setEditingTariff] = useState(null);

  useEffect(() => {
    const cacheBust = Date.now();
    fetch('/api/tariffs')
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load tariffs");
        return r.json();
      })
      .then((data) => setTariffs(data))
      .catch((err) => {
        console.error("Could not load tariffs.json", err);
        setTariffs([]);
      });
  }, []);

  // Debounce für Ajax-Suche (1 Sekunde, ab 3 Zeichen)
  useEffect(() => {
    if (ajaxTimerRef.current) clearTimeout(ajaxTimerRef.current);
    if (ajaxSearchInput.trim().length < 3) {
      setAjaxSearchResults([]);
      setShowAjaxDropdown(false);
      return;
    }
    ajaxTimerRef.current = setTimeout(() => {
      const q = ajaxSearchInput.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

      // "spielgeldrechner" / "spielgeld" → direkt zum Spielgeldrechner navigieren
      if (q.includes("spielgeld") || q.includes("coupon") || q.includes("zuschuss")) {
        setAjaxSearchResults([{ id: '__spielgeld__', title: 'Spielgeldrechner', category: 'Tool', segment: '__spielgeld__', dataLabel: 'Spielgeld-/Coupon-Rechner öffnen' }]);
        setShowAjaxDropdown(true);
        return;
      }

      // "brutto" / "netto" / "mwst" → Brutto-Netto-Rechner
      if (q.includes("brutto") || q.includes("netto") || q.includes("mwst") || q.includes("mehrwert")) {
        setAjaxSearchResults([{ id: '__bruttonetto__', title: 'Brutto/Netto Rechner', category: 'Tool', segment: '__bruttonetto__', dataLabel: 'Brutto-Netto-Rechner öffnen' }]);
        setShowAjaxDropdown(true);
        return;
      }

      const results = tariffs.filter((t) => {
        const n = (v) => (v ?? "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        return n(t.title).includes(q) ||
          n(t.category).includes(q) ||
          n(t.dataLabel).includes(q) ||
          (Array.isArray(t.features) && t.features.some((f) => n(f).includes(q))) ||
          (t.price != null && t.price.toString().includes(q));
      }).slice(0, 8);
      setAjaxSearchResults(results);
      setShowAjaxDropdown(results.length > 0);
    }, 1000);
    return () => { if (ajaxTimerRef.current) clearTimeout(ajaxTimerRef.current); };
  }, [ajaxSearchInput, tariffs]);

  // Click-Outside schließt Dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ajaxSearchRef.current && !ajaxSearchRef.current.contains(e.target)) {
        setShowAjaxDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const segments = [
    { id: "mobile", label: "Mobile" },
    { id: "blue", label: "Blue" },
    { id: "prepaid", label: "Prepaid" },
    { id: "myhome", label: "my Home" },
    { id: "kombivorteil", label: "Kombi-Vorteil" },
  ];

  // Hilfsfunktion: diakritische Zeichen entfernen und kleinschreiben
  const normalize = (v) =>
    (v ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const mobileGroups = [
    { id: "alle", label: "Alle", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    )},
    { id: "promo", label: "Promo", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6v6l4 2"/>
        <path d="m16 16-4-4"/>
      </svg>
    )},
    { id: "classic", label: "Classic", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M3 9h18"/>
        <path d="M9 21V9"/>
      </svg>
    )},
    { id: "kombi", label: "Kombi-Vorteil", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M16 3h5v5"/>
        <path d="M8 3H3v5"/>
        <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"/>
        <path d="m15 9 6-6"/>
        <path d="M16 21h5v-5"/>
        <path d="M8 21H3v-5"/>
      </svg>
    )},
    { id: "young", label: "Young People", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <circle cx="8" cy="8" r="3"/>
        <circle cx="16" cy="8" r="3"/>
        <path d="M4 20c0-3 2-5 4-5s4 2 4 5"/>
        <path d="M12 20c0-3 2-5 4-5s4 2 4 5"/>
      </svg>
    )},
    { id: "60plus", label: "60+", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <circle cx="12" cy="12" r="9"/>
        <path d="M9 10.5a2 2 0 1 0 0 3"/>
        <path d="M15 11.5v4"/>
        <path d="M13 13.5h4"/>
      </svg>
    )},
    { id: "business", label: "Business", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="3" y="10" width="7" height="11" rx="1"/>
        <rect x="14" y="3" width="7" height="18" rx="1"/>
        <path d="M6.5 13h.01"/>
        <path d="M6.5 16h.01"/>
        <path d="M17.5 7h.01"/>
        <path d="M17.5 11h.01"/>
        <path d="M17.5 15h.01"/>
      </svg>
    )},
  ];

  // kombiGroups entfernt – keine Unterbereiche in Kombi-Ansicht

  const myHomeTechGroups = [
    { id: "all", label: "Alle", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M3 9h18"/>
        <path d="M9 21V9"/>
      </svg>
    )},
    { id: "kombi", label: "Kombi-Vorteil", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M16 3h5v5"/>
        <path d="M8 3H3v5"/>
        <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"/>
        <path d="m15 9 6-6"/>
        <path d="M16 21h5v-5"/>
        <path d="M8 21H3v-5"/>
      </svg>
    )},
    { id: "vdsl", label: "VDSL", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <rect x="2" y="7" width="20" height="14" rx="2"/>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
      </svg>
    )},
    { id: "kabel", label: "Kabel", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M4 9a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2"/>
        <path d="M4 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9"/>
        <line x1="12" y1="7" x2="12" y2="13"/>
      </svg>
    )},
    { id: "glasfaser", label: "Glasfaser", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        <path d="M12 11l-4 8"/>
      </svg>
    )},
    { id: "homespot", label: "Home Spot", icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <circle cx="12" cy="20" r="1"/>
      </svg>
    )},
  ];

  // Memoized Filterung - verhindert unnötige Re-Berechnungen
  const filteredTariffs = useMemo(() => {
    return tariffs.filter((t) => {
    // Suche greift global über alle Segmente und Gruppen
    if (searchQuery.trim()) {
      const q = normalize(searchQuery);
      const matchesSearch =
        normalize(t.title).includes(q) ||
        normalize(t.category).includes(q) ||
        normalize(t.dataLabel).includes(q) ||
        (Array.isArray(t.features) && t.features.some((f) => normalize(f).includes(q))) ||
        (t.price != null && t.price.toString().includes(q));

      return !!matchesSearch;
    }

    // Brand-Filter: wenn aktiv, auf Titel/Category/Group prüfen
    if (activeBrand === 'o2') {
      const tt = normalize(t.title || '');
      const tc = normalize(t.category || '');
      const tg = normalize(t.group || '');
      if (!(tt.includes('o2') || tc.includes('o2') || tg.includes('blue'))) return false;
    }
    if (activeBrand === 'superselect') {
      const tt = normalize(t.title || '');
      const tc = normalize(t.category || '');
      const tg = normalize(t.group || '');
      if (!(tt.includes('super') || tc.includes('super') || tg.includes('superselect') || tt.includes('select'))) return false;
    }

    if (activeSegment === "kombivorteil") {
      // Dedizierte Kombi-Vorteil Tarife aus tariffs.json nutzen (Mobile 50% + Home 10€)
      return t.segment === "kombivorteil";
    }

    // Sonderfall: In Mobile-Ansicht "Alle" soll Mobile + Blue zeigen (alle Gruppen)
    if (activeSegment === "mobile" && activeMobileGroup === "alle") {
      return t.segment === "mobile" || t.segment === "blue";
    }

    // Sonderfall: Mobile Kombi-Vorteil - zeigt nur Classic Tarife (werden dann mit Rabatt angezeigt)
    if (activeSegment === "mobile" && activeMobileGroup === "kombi") {
      return t.segment === "mobile" && t.group === "classic";
    }

    if (t.segment !== activeSegment) return false;

    if (activeSegment === "mobile") {
      return t.group === activeMobileGroup;
    }

    if (activeSegment === "myhome") {
      // Sonderfall: MyHome Kombi-Vorteil - zeigt Tarife die Kombi-rabattfähig sind (z.B. mit Internet-Rabatt)
      if (activeMyHomeTech === "kombi") {
        // Zeige alle MyHome Tarife als kombi-rabattfähig
        return true;
      }
      if (activeMyHomeTech === "all") return true;
      return t.group === activeMyHomeTech;
    }

    return true;
  }).map((t) => {
    // Apply combo discounts für Mobile Classic in der Mobile Kombi-Ansicht
    if (activeSegment === "mobile" && activeMobileGroup === "kombi") {
      if (t.segment === "mobile" && t.group === "classic") {
        const tariff = { ...t };
        tariff.originalPrice = tariff.price;
        const discountedHalf = Math.floor(tariff.price * 50) / 100;
        tariff.price = discountedHalf.toFixed(2);
        tariff.isComboDiscounted = true;
        tariff.comboDiscount = "50%";
        // Titel mit KV-Suffix ergänzen
        tariff.title = tariff.title + " KV";

        const titleStr = (tariff.title || "").toLowerCase();
        const isMobileLOrAbove = titleStr.includes("mobile l") || titleStr.includes("mobile xl") || titleStr.includes("unlimited");
        if (isMobileLOrAbove) {
          tariff.connectionFee = 0;
        }

        return tariff;
      }
      return t;
    }
    
    // Apply combo discounts für MyHome in der MyHome Kombi-Ansicht
    if (activeSegment === "myhome" && activeMyHomeTech === "kombi") {
      const tariff = { ...t };
      // 10€ Rabatt für Kombi mit Mobilfunk
      tariff.originalPrice = tariff.price;
      tariff.price = (parseFloat(tariff.price) - 10).toFixed(2);
      tariff.isComboDiscounted = true;
      tariff.comboDiscount = "10€";
      // Titel mit KV-Suffix ergänzen
      tariff.title = tariff.title + " KV";
      return tariff;
    }
    
    // Dedizierte Kombi-Vorteil Tarife aus JSON: isComboDiscounted setzen, Segment für korrektes Rendering anpassen
    if (activeSegment === "kombivorteil") {
      if (t.segment === "kombivorteil") {
        const tariff = { ...t };
        tariff.isComboDiscounted = true;
        // Preis auf promoPrice setzen (= rabattierter Preis im JSON)
        if (tariff.promoPrice != null && tariff.promoPrice < tariff.price) {
          tariff.originalPrice = tariff.kombiOriginalPrice || tariff.price;
          tariff.price = tariff.promoPrice;
        }
        // Segment für korrekte Icon-/Label-Darstellung in TariffCard überschreiben
        const homeGroups = ['vdsl', 'kabel', 'glasfaser', 'homespot'];
        if (homeGroups.includes(tariff.group)) {
          tariff.segment = 'myhome';
        } else if (tariff.group === 'mobile') {
          tariff.segment = 'mobile';
        }
        return tariff;
      }
      return t;
    }
    
    // Globale Regel: Young (Junge Leute) Tarife ohne Anschlussgebühr
    const normalTariff = { ...t };
    if (normalTariff.segment === "mobile" && normalTariff.group === "young") {
      normalTariff.connectionFee = 0;
    }
    return normalTariff;
  }).sort((a, b) => {
    const groupOrder = { promo: 0, classic: 1, young: 2, "60plus": 3, business: 4, mobile: 5, vdsl: 6, kabel: 7, glasfaser: 8, homespot: 9 };
    const titleOrder = {
      "O2 Mobile S": 0,
      "O2 Mobile M": 1,
      "O2 Mobile L": 2,
      "O2 Mobile XL": 3,
      "O2 Mobile Unlimited Smart": 4,
      "O2 Mobile Unlimited on Demand": 5,
      "O2 Mobile Unlimited Max": 6,
      "O2 Mobile S Young": 10,
      "O2 Mobile M Young": 11,
      "O2 Mobile L Young": 12,
      "O2 Mobile XL Young": 13,
      "O2 Mobile Unlimited Smart Young": 14,
      "O2 Mobile Unlimited on Demand Young": 15,
      "O2 Mobile Unlimited Max Young": 16,
      "O2 Mobile S 60+": 20,
      "O2 Mobile M 60+": 21,
      "O2 Mobile L 60+": 22,
      "O2 Mobile XL 60+": 23,
      "O2 Mobile Unlimited Smart 60+": 24,
      "O2 Mobile Unlimited on Demand 60+": 25,
      "O2 Mobile Unlimited Max 60+": 26,
      "O2 Prepaid Basic": 0,
      "O2 Prepaid S": 1,
      "O2 Prepaid M": 2,
      "O2 Prepaid L": 3,
      "O2 Prepaid Max": 4,
    };

    // Primär: Gruppe
    const go = (groupOrder[a.group] ?? 99) - (groupOrder[b.group] ?? 99);
    if (go !== 0) return go;

    // Innerhalb Mobile oder Prepaid: feste Reihenfolge per Titel
    if ((a.segment === "mobile" && b.segment === "mobile") || (a.segment === "prepaid" && b.segment === "prepaid")) {
      const ao = titleOrder[a.title] ?? 50;
      const bo = titleOrder[b.title] ?? 50;
      if (ao !== bo) return ao - bo;
    }

    // Fallback: nach Datenlabel (Zahl extrahieren, "unbegrenzt" = 1000)
    const extractNumeric = (label) => {
      if (typeof label === "number") return label;
      if (label === "unbegrenzt") return 1000;
      const match = String(label).match(/(\d+)/);
      return match ? parseInt(match[1]) : 9999;
    };
    const av = extractNumeric(a.dataLabel);
    const bv = extractNumeric(b.dataLabel);
    return av - bv;
  });
  }, [tariffs, searchQuery, activeSegment, activeMobileGroup, activeMyHomeTech, activeBrand]);
  
  const uniqueSegments = useMemo(() => Array.from(new Set(tariffs.map(t => t.segment).filter(Boolean))), [tariffs]);
  const uniqueGroups = useMemo(() => Array.from(new Set(tariffs.map(t => t.group).filter(Boolean))), [tariffs]);
  const uniqueCategories = useMemo(() => Array.from(new Set(tariffs.map(t => t.category).filter(Boolean))), [tariffs]);

  const openEditById = (id) => {
    console.log("openEditById called with id:", id);
    const base = tariffs.find((x) => x.id === id);
    console.log("Found tariff:", base);
    if (base) setEditingTariff(base);
  };

  const handleSaveTariff = (draft) => {
    setTariffs((prev) => prev.map((t) => (t.id === draft.id ? { ...t, ...draft } : t)));
    setEditingTariff(null);
  };

  // Keine Persistenz für aktive Ansicht
  
  // Ausgewählte Tarife für Selection/LiveView Panel
  const selectedTariffs = useMemo(() => 
    tariffs.filter(t => selectedTariffIds.includes(t.id)), 
    [tariffs, selectedTariffIds]
  );

  // === ROUTER RENDERING (TEF DESIGNER) ===
  // Bevor die große Legacy App geladen wird, prüfen wir unsere Router-Ergebnisse:
  if (isRouting && !useLegacyMode) {
    return <AppShell isRouting={true} />;
  }

  // Erfolgsfall: Die angefragte Seite existiert im TEF Designer Format!
  if (routedPageId && !useLegacyMode) {
    return (
      <div className="tef-live-frontend" style={{ minHeight: '100vh', background: '#fff' }}>
        <PageRenderer pageId={routedPageId} />
      </div>
    );
  }

  // === FALLBACK: KLASSISCHES REACT RENDERING ===
  return (
    <AppShell>
      {/* Globaler Fixed Header (einheitlich für Dashboard + Tarife) */}
      {!activeSection.startsWith('/p/') && (
        <Header
          activeEvent={eventSettings.activeEvent}
          snowIntensity={eventSettings.snowIntensity}
          bubbleIntensity={eventSettings.bubbleIntensity}
          showCountdown={eventSettings.eventOptions?.silvester?.countdown !== false}
          showBrandLogos={eventSettings.showBrandLogos}
          onEventChange={(eventKey) => handleEventSettingsChange({ ...eventSettings, activeEvent: eventKey })}
          effects={eventSettings.effects}
          onEffectsChange={(newEffects) => handleEventSettingsChange({ ...eventSettings, effects: newEffects })}
          selectedTariffs={selectedTariffs}
          onRemoveTariff={(id) => handleSelectToggle(id)}
          onClearSelection={handleClearSelection}
          onOpenLiveView={() => setLiveViewOpen(true)}
          onAdminOpen={() => setAdminPanelOpen(true)}
          appSection={activeSection}
          onAppSectionChange={setActiveSection}
          menuButtons={eventSettings.menuButtons}
          onHeaderHeight={(h) => { 
            document.documentElement.style.setProperty('--header-height', h + 'px'); 
          }}
        />
      )}

      {/* Live View Panel (rechts) */}
      {!activeSection.startsWith('/p/') && (
        <LiveViewPanel
          isOpen={liveViewOpen}
          onClose={() => setLiveViewOpen(false)}
          selectedTariffs={selectedTariffs}
        />
      )}

      {/* MAIN CONTENT */}
      <MainContent showHeaderSpacer={!activeSection.startsWith('/p/') && activeSection !== 'spielgeld'}>
        <AppRoutes
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          tariffs={tariffs}
          selectedTariffIds={selectedTariffIds}
          eventSettings={eventSettings}
          setAdminPanelOpen={setAdminPanelOpen}
        >
          {/* ========== TARIFE SECTION ========== */}
          <>
      <section className="flex-1 px-4 sm:px-8 lg:px-12 pt-0 pb-8">
        <div className="w-full flex flex-col gap-6">
          {/* Tab-Bar (kompakt, zentriert) + Ajax-Suche rechts */}
          <div className="flex items-start gap-3">
            {/* Zentrierte Tabs */}
            <div className="flex-1 flex flex-wrap items-center justify-center gap-1.5">
              {/* Brand Area Buttons */}
              <div className="inline-flex gap-1 bg-slate-900/60 border border-slate-700/60 rounded-full px-1 py-1">
                <button
                  type="button"
                  onClick={() => setActiveBrand("all")}
                  aria-pressed={activeBrand === "all"}
                  className={
                    "px-2.5 py-1 rounded-full text-[0.6rem] font-bold tracking-[0.08em] uppercase transition " +
                    (activeBrand === "all"
                      ? "bg-sky-400 text-slate-900"
                      : "text-sky-200/80 hover:bg-slate-800/70")
                  }
                >
                  Alle
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveBrand("o2"); setActiveSegment("mobile"); }}
                  aria-pressed={activeBrand === "o2"}
                  className={
                    "px-2.5 py-1 rounded-full text-[0.6rem] font-extrabold tracking-[0.06em] transition " +
                    (activeBrand === "o2"
                      ? "bg-blue-600 text-white shadow-[0_0_12px_rgba(0,102,204,0.4)]"
                      : "text-blue-300/80 hover:bg-blue-900/50")
                  }
                >
                  o2
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveBrand("superselect"); setActiveSegment("mobile"); }}
                  aria-pressed={activeBrand === "superselect"}
                  className={
                    "px-2.5 py-1 rounded-full text-[0.6rem] font-extrabold tracking-[0.06em] transition " +
                    (activeBrand === "superselect"
                      ? "bg-red-600 text-white shadow-[0_0_12px_rgba(204,51,51,0.4)]"
                      : "text-red-300/80 hover:bg-red-900/50")
                  }
                >
                  Super Select
                </button>
              </div>

              <div className="inline-flex flex-wrap gap-1 bg-slate-900/60 border border-sky-700/60 rounded-full px-1.5 py-1">
                {segments.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveSegment(s.id)}
                    aria-pressed={activeSegment === s.id}
                    aria-label={`Segment ${s.label}`}
                    className={
                      "px-2.5 py-1 rounded-full text-[0.6rem] font-semibold tracking-[0.1em] uppercase transition " +
                      (activeSegment === s.id
                        ? "bg-sky-400 text-slate-900 segment-tab-active"
                        : "text-sky-200/80 hover:bg-sky-800/70")
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {activeSegment === "mobile" && (
                <div className="inline-flex flex-wrap gap-1 bg-slate-900/50 border border-sky-800/60 rounded-full px-1.5 py-1">
                  {mobileGroups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setActiveMobileGroup(g.id)}
                      aria-pressed={activeMobileGroup === g.id}
                      aria-label={`Mobilfunkgruppe: ${g.label}`}
                      className={
                        "px-2 py-0.5 rounded-full text-[0.6rem] font-semibold tracking-[0.1em] uppercase transition flex items-center gap-1 " +
                        (activeMobileGroup === g.id
                          ? "bg-sky-500 text-slate-900 segment-tab-active"
                          : "text-sky-200/80 hover:bg-sky-800/70")
                      }
                    >
                      {g.icon}
                      {g.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Kombi-Unterbereiche entfernt: keine Tabs in Kombi-Ansicht */}

              {activeSegment === "myhome" && (
                <div className="inline-flex flex-wrap gap-1 bg-slate-900/50 border border-sky-800/60 rounded-full px-1.5 py-1">
                  {myHomeTechGroups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setActiveMyHomeTech(g.id)}
                      aria-pressed={activeMyHomeTech === g.id}
                      aria-label={`Technologie: ${g.label}`}
                      className={
                        "px-2 py-0.5 rounded-full text-[0.6rem] font-semibold tracking-[0.1em] uppercase transition flex items-center gap-1 " +
                        (activeMyHomeTech === g.id
                          ? "bg-sky-500 text-slate-900 segment-tab-active"
                          : "text-sky-200/80 hover:bg-sky-800/70")
                      }
                    >
                      {g.icon}
                      {g.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Ajax-Suche rechts mit Glow-Glass-Effekt */}
            <div ref={ajaxSearchRef} className="relative flex-shrink-0 w-56 lg:w-64">
              <div className="relative">
                <input
                  type="text"
                  value={ajaxSearchInput}
                  onChange={(e) => {
                    setAjaxSearchInput(e.target.value);
                    if (e.target.value.trim().length < 3) {
                      setSearchQuery("");
                    }
                  }}
                  placeholder="Tarif suchen…"
                  className="w-full px-3 py-1.5 pl-8 rounded-full text-sky-50 placeholder-sky-300/50 focus:outline-none transition-all text-xs bg-slate-900/50 border border-sky-700/40 focus:border-sky-400/60 focus:shadow-[0_0_15px_rgba(56,189,248,0.15)]"
                />
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-sky-400/70"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                {ajaxSearchInput && (
                  <button
                    onClick={() => { setAjaxSearchInput(""); setSearchQuery(""); setShowAjaxDropdown(false); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sky-400/60 hover:text-sky-200 transition-colors"
                    aria-label="Suche löschen"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                )}
              </div>
              {/* Glow-Glass Dropdown */}
              {showAjaxDropdown && ajaxSearchResults.length > 0 && (
                <div className="absolute right-0 top-full mt-2 w-72 z-50 rounded-xl overflow-hidden ajax-search-dropdown">
                  <div className="py-1">
                    {ajaxSearchResults.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-white/10 transition-colors cursor-pointer"
                        onClick={() => {
                          if (t.id === '__spielgeld__') {
                            setActiveSection("spielgeld");
                            setShowAjaxDropdown(false);
                            setAjaxSearchInput("");
                            return;
                          }
                          if (t.id === '__bruttonetto__') {
                            setActiveSection("bruttonetto");
                            setShowAjaxDropdown(false);
                            setAjaxSearchInput("");
                            return;
                          }
                          setSearchQuery(t.title);
                          setActiveSegment(t.segment === "kombivorteil" ? "kombivorteil" : t.segment);
                          setShowAjaxDropdown(false);
                          setAjaxSearchInput(t.title);
                        }}
                      >
                        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-sky-500/20 border border-sky-400/30 flex items-center justify-center text-[0.6rem] font-bold text-sky-300 uppercase">
                          {t.id === '__spielgeld__' ? '💰' : t.id === '__bruttonetto__' ? '💱' : t.segment === "mobile" ? "M" : t.segment === "blue" ? "B" : t.segment === "prepaid" ? "P" : t.segment === "myhome" ? "H" : "K"}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-sky-100 truncate">{t.title}</div>
                          <div className="text-[0.6rem] text-sky-300/60">{t.category} · {typeof t.price === 'number' ? t.price.toFixed(2).replace('.', ',') + ' €' : t.dataLabel}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {activeSegment === "kombivorteil" ? (
            <div className="tariff-grid-container">
              {filteredTariffs.map((t) => (
                <TariffCard
                  key={t.id}
                  {...t}
                  isComboDiscounted={true}
                  kombiOriginalPrice={t.price}
                  activeEvent={eventSettings.activeEvent}
                  effects={eventSettings.effects}
                  effectConfig={eventSettings.effectConfig}
                  isActive={activeCardIds.includes(t.id)}
                  isSelected={selectedTariffIds.includes(t.id)}
                  onSelectToggle={() => handleSelectToggle(t.id)}
                  onPrint={() => handlePrintSingle(t.id)}
                  onToggle={(next) => {
                    setActiveCardIds((prev) => {
                      if (next) {
                        return prev.includes(t.id) ? prev : [...prev, t.id];
                      }
                      return prev.filter((x) => x !== t.id);
                    });
                  }}
                  onEditRequest={openEditById}
                />
              ))}
            </div>
          ) : (
            <div className="tariff-grid-container mt-6">
              {filteredTariffs.map((t) => (
                <TariffCard
                  key={t.id}
                  {...t}
                  activeEvent={eventSettings.activeEvent}
                  effects={eventSettings.effects}
                  effectConfig={eventSettings.effectConfig}
                  isActive={activeCardIds.includes(t.id)}
                  isSelected={selectedTariffIds.includes(t.id)}
                  onSelectToggle={() => handleSelectToggle(t.id)}
                  onPrint={() => handlePrintSingle(t.id)}
                  onToggle={(next) => {
                    setActiveCardIds((prev) => {
                      if (next) {
                        return prev.includes(t.id) ? prev : [...prev, t.id];
                      }
                      return prev.filter((x) => x !== t.id);
                    });
                  }}
                  onEditRequest={openEditById}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      </>
      </AppRoutes>
      </MainContent>
      
      {/* Admin-Panel mit Lazy Loading */}
      <Suspense fallback={<div className="loading-spinner">Laden...</div>}>
        <EventAdminPanel
          isOpen={adminPanelOpen}
          onClose={() => setAdminPanelOpen(false)}
          eventSettings={eventSettings}
          onSettingsChange={handleEventSettingsChange}
          tariffs={tariffs}
          onEditTariff={openEditById}
          onTariffsChange={setTariffs}
        />
      </Suspense>
      
      {/* Edit-Modal */}
      {editingTariff && (
        <TariffEditModal
          tariff={editingTariff}
          onClose={() => setEditingTariff(null)}
          onSave={handleSaveTariff}
          segments={uniqueSegments}
          groups={uniqueGroups}
          categories={uniqueCategories}
          activeEvent={eventSettings.activeEvent}
        />
      )}
    </AppShell>
  );
}

// Auth-Gate: Zeigt Login-Screen wenn nicht authentifiziert, sonst die App
function AppWithAuth() {
  const { user, loading, isAuthenticated, login, login2FA, logout, error } = useAuth();

  // Ladebildschirm während Session-Check
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at top, #063c88 0, #02122b 45%, #010817 100%)',
        color: '#64748b',
        fontFamily: "'Poppins', sans-serif",
        fontSize: '0.9rem',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#38bdf8', marginBottom: '12px' }}>TEF</div>
          <div>Wird geladen...</div>
        </div>
      </div>
    );
  }

  // Login-Screen wenn nicht authentifiziert
  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} onLogin2FA={login2FA} error={error} />;
  }

  // Authentifiziert -> App laden
  return (
    <MarketProvider>
      <App user={user} onLogout={logout} />
    </MarketProvider>
  );
}

export { AppWithAuth as App };
/* Cache Buster: 1764549200 */
